import React, { useMemo } from 'react';
import {
  PROFILE_BANNER_LINE_HEIGHT,
  profileBannerCopy,
  profileBannerLayout,
  type ProfileBannerFacts,
  type ProfileBannerText,
  type ProfileTextKind,
} from 'fym-shared/src/music24/profileBanner';
import { KIT_FONT_FACES, type ReleaseThemeId, type ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { useKitWebFonts } from '../../themes/kitWebFonts';

/** What the banner text of a preview needs: the variant, the facts, the day, the look and the cover. */
export interface BannerTextPreview {
  variant: ProfileBannerText;
  facts: ProfileBannerFacts;
  today: string;
  themeId: ReleaseThemeId;
  coverUrl: string | null;
}

interface BannerTextLayerProps {
  kind: ProfileTextKind;
  /** The file size: the layer is drawn at file pixels inside the frame canvas. */
  size: { width: number; height: number };
  preview: BannerTextPreview;
  colours: ThemeColours;
}

/**
 * The text of a Profile banner in the tool preview (design profile-card b1, b2): the layout of
 * fym-shared `profileBannerLayout`, the one that the server renders, drawn with the kit web fonts.
 */
const BannerTextLayerComponent: React.FC<BannerTextLayerProps> = ({ kind, size, preview, colours }) => {
  useKitWebFonts();
  const layout = useMemo(() => {
    const copy = profileBannerCopy(preview.variant, preview.facts, preview.today);
    return copy === null
      ? null
      : profileBannerLayout({
          kind,
          size,
          copy,
          look: { themeId: preview.themeId },
          hasCover: preview.coverUrl !== null,
        });
  }, [kind, preview, size]);
  if (layout === null) {
    return null;
  }
  return (
    <div aria-hidden="true" data-testid={`profile-banner-text-${kind}`}>
      {layout.cover !== null && preview.coverUrl !== null && (
        <img
          src={preview.coverUrl}
          alt=""
          style={{
            position: 'absolute',
            left: layout.cover.x,
            top: layout.cover.y,
            width: layout.cover.size,
            height: layout.cover.size,
            borderRadius: layout.cover.radius,
            objectFit: 'cover',
          }}
        />
      )}
      {layout.texts.map((item) => {
        const colour = item.colour === 'accent' ? colours.accent : colours.text;
        const face = KIT_FONT_FACES[item.fontId];
        return (
          <span
            key={item.role}
            data-role={item.role}
            style={{
              position: 'absolute',
              left: item.x,
              top: item.y,
              maxWidth: item.maxWidth,
              fontFamily: `'${face.family}', ${face.fallback}`,
              fontSize: item.sizePx,
              fontWeight: item.style.weight,
              fontStyle: item.style.italic || face.italic ? 'italic' : 'normal',
              letterSpacing: `${item.style.tracking}em`,
              lineHeight: PROFILE_BANNER_LINE_HEIGHT,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              color: colour,
              opacity: item.alpha,
              textShadow: item.glow ? `0 0 ${Math.round(item.sizePx * 0.3)}px ${colour}` : undefined,
            }}
          >
            {item.text}
          </span>
        );
      })}
    </div>
  );
};

export const BannerTextLayer = TypedMemo(BannerTextLayerComponent);
